import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CountryInfo, PhoneNumberInfo } from 'src/app/interfaces/country-info.interface';
import { CountriesService } from 'src/app/services/countries.service';
import { PhoneNumberService } from 'src/app/services/phone-number.service';
import { SecondAuthLevelService } from '../../../../auth/second-auth-level.service';
import { promiseWithCatch, safelyDetectChanges } from '../../../../common/app.helpers';
import ProjectLinks from '../../../../common/project.links';
import { ProfileFormTypes } from '../../../forms.enum';
import { FormFieldSate } from '../form-field.state';

@Component({
  selector: 'app-phone-form-field',
  templateUrl: './phone-form-field.component.html',
  styleUrls: ['./phone-form-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhoneFormFieldComponent extends FormFieldSate implements AfterViewChecked {
  public selectedCountryPhoneInfo: PhoneNumberInfo;

  public timerIsRunning = false;
  public disableChangePhoneNumber = false;
  public showChangePhoneNumberBtn = false;

  public phoneNumber = '';
  public searchValue = '';
  public placeholder = '';
  public phoneNumberBtnHint = '';

  public countries: CountryInfo[] = [];
  public countriesBeg: CountryInfo[] = [];

  private phoneNumberService = inject(PhoneNumberService);
  private secondAuthLevelService = inject(SecondAuthLevelService);
  private countriesService = inject(CountriesService);
  private translateService = inject(TranslateService);
  private changeDetector = inject(ChangeDetectorRef);
  private router = inject(Router);

  public override ngOnInit(): void {
    super.ngOnInit();
    this.setupStartValue();
    this.subscribeOnCodeCountdownTimer();
  }

  public ngAfterViewChecked(): void {
    safelyDetectChanges(this.changeDetector);
  }

  public getPhoneCode(country: CountryInfo): string {
    return this.phoneNumberService.getCountryPhoneCode(country.iso);
  }

  public filterDropdown(e: string): void {
    const searchString: string = e.toLowerCase();

    if (!searchString) {
      this.countries = this.countriesBeg.slice();
      return;
    }
    this.countries = this.countriesBeg.filter(
      (country: CountryInfo) =>
        country.name.toLowerCase().indexOf(searchString) > -1 ||
        this.getPhoneCode(country).toLowerCase().indexOf(searchString) > -1,
    );
  }

  public onSelectedCountry(country: CountryInfo): void {
    this.selectedCountryPhoneInfo = this.phoneNumberService.getCountryPhoneNumberInfo(country.iso);
    this.updateValidators(country);
  }

  public onChanges(value: string): void {
    this.markControlAsFilledIn();
    this.updateControl(value);
  }

  public replacePhoneNumberForPlaceholder(): string {
    const CHARACTER_FOR_PHONE_NUMBER_PLACEHOLDER = 'x';
    return this.selectedCountryPhoneInfo.sampleE164Phone.replace(/\d/g, CHARACTER_FOR_PHONE_NUMBER_PLACEHOLDER);
  }

  public changePhoneNumber(): void {
    const previousPage: string = ProjectLinks.LINK_PROFILE_EDIT;
    const changePhone = true;

    promiseWithCatch(
      this.router.navigate([ProjectLinks.LINK_SECOND_AUTH_LEVEL], { state: { changePhone, previousPage } }),
    );
  }

  public removeFieldValue(): void {
    this.secondAuthLevelService.resetPhoneNumberInput();
    this.disableChangePhoneNumber = false;
    this.clearField();
  }

  public hasValidValue(): boolean {
    return this.control.valid && (this.control.value as boolean) && this.control.value !== '+1';
  }

  private clearField(): void {
    this.clearCountriesSearch();

    this.selectDefaultCountry();
    this.phoneNumber = '';
    this.control.reset();
    this.changeDetector.detectChanges();
  }

  private clearCountriesSearch(): void {
    this.searchValue = '';
    this.countries = this.countriesBeg.slice();
  }

  private subscribeOnCodeCountdownTimer(): void {
    this.secondAuthLevelService.codeCountdownTimer.subscribe((countNumber: number) => {
      this.timerIsRunning = countNumber > 1000;
      this.disableChangePhoneNumber = this.timerIsRunning;
    });
  }

  private getFormattedPhoneNumber(value: string): string {
    return `${this.selectedCountryPhoneInfo.phoneCode}${value}`;
  }

  private setupStartValue(): void {
    this.disableChangePhoneNumber = this.showChangePhoneNumberBtn =
      this.fieldAttribute.backendAttribute.scheme_name === ProfileFormTypes.PROFILE;

    this.phoneNumberBtnHint = this.translateService.instant(
      (this.control.value as string) ? 'PROFILE_CHANGE_PHONE_NUMBER_BTN' : 'PROFILE_ADD_PHONE_NUMBER_BTN',
    ) as string;

    this.countries = this.countriesService.getAllCountriesInfo();
    this.countriesBeg = this.countriesService.getAllCountriesInfo();

    if (!this.control.value) {
      this.selectDefaultCountry();
    } else {
      if (this.phoneNumberService.isPossibleNumberString(this.control.value as string)) {
        const iso: string = this.phoneNumberService.getCountryISO(this.control.value as string);

        this.phoneNumber = this.phoneNumberService.getNationalNumber(this.control.value as string);

        this.onSelectedCountry(this.countries.find((item: CountryInfo) => item.iso === iso));

        this.markControlAsFilledIn();
      } else {
        this.selectDefaultCountry();
      }
    }
  }

  private selectDefaultCountry() {
    this.onSelectedCountry(this.countries.find((item: CountryInfo) => item.iso === 'US'));
  }

  private updateValidators(country: CountryInfo): void {
    const phoneValidators: ValidatorFn[] = this.formValidationService.getPhoneValidators(country);

    const validators: ValidatorFn[] = this.fieldAttribute.backendAttribute.required
      ? [Validators.required, ...phoneValidators]
      : phoneValidators;

    this.control.setValidators(Validators.compose(validators));
    this.updateControl(this.phoneNumber);
    this.control.updateValueAndValidity();
  }

  private updateControl(value: string): void {
    if (this.selectedCountryPhoneInfo) {
      this.control.setValue(this.getFormattedPhoneNumber(value), { emitEvent: true });
    }
  }

  private markControlAsFilledIn(): void {
    this.control.markAsPristine();
    this.control.markAsTouched();
  }
}
